import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  HomeModernIcon,
  BellIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

interface AdminStats {
  users: {
    total: number;
    active: number;
    new_this_week: number;
  };
  families: {
    total: number;
    total_memberships: number;
  };
  notifications: {
    total: number;
    unread: number;
  };
  activities: {
    last_24h: number;
  };
}

interface LogStats {
  period_hours: number;
  total_requests: number;
  unique_users: number;
  unique_ips: number;
  error_count: number;
  avg_response_time_ms: number;
  requests_by_method: Record<string, number>;
  top_paths: { path: string; count: number }[];
  top_users: { username: string; count: number }[];
}

function StatCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  color = 'amber',
  link 
}: { 
  title: string; 
  value: number | string; 
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'amber' | 'green' | 'blue' | 'purple' | 'red';
  link?: string;
}) {
  const colorClasses = {
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  const content = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-5 rounded-xl border ${colorClasses[color]} ${link ? 'hover:scale-[1.02] cursor-pointer' : ''} transition-transform`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-surface-400">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-surface-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colorClasses[color].split(' ')[0]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </motion.div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

export default function AdminDashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const response = await api.get('/admin/stats');
      return response.data as AdminStats;
    },
  });

  const { data: logStats, isLoading: logStatsLoading } = useQuery({
    queryKey: ['admin', 'logs', 'stats'],
    queryFn: async () => {
      const response = await api.get('/admin/logs/stats', { params: { hours: 24 } });
      return response.data as LogStats;
    },
  });

  if (statsLoading || logStatsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-surface-400 mt-1">System overview and statistics</p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.users.total || 0}
          subtitle={`${stats?.users.active || 0} active`}
          icon={UsersIcon}
          color="blue"
          link="/admin/users"
        />
        <StatCard
          title="New This Week"
          value={stats?.users.new_this_week || 0}
          subtitle="New registrations"
          icon={ArrowTrendingUpIcon}
          color="green"
        />
        <StatCard
          title="Families"
          value={stats?.families.total || 0}
          subtitle={`${stats?.families.total_memberships || 0} memberships`}
          icon={HomeModernIcon}
          color="purple"
          link="/admin/families"
        />
        <StatCard
          title="Notifications"
          value={stats?.notifications.total || 0}
          subtitle={`${stats?.notifications.unread || 0} unread`}
          icon={BellIcon}
          color="amber"
        />
      </div>

      {/* Activity Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-5 rounded-xl bg-surface-800/50 border border-surface-700"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-amber-400" />
            Last 24 Hours
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-surface-700/50">
              <p className="text-2xl font-bold text-white">{logStats?.total_requests || 0}</p>
              <p className="text-xs text-surface-400">Total Requests</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-700/50">
              <p className="text-2xl font-bold text-white">{logStats?.unique_users || 0}</p>
              <p className="text-xs text-surface-400">Unique Users</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-700/50">
              <p className="text-2xl font-bold text-white">{logStats?.unique_ips || 0}</p>
              <p className="text-xs text-surface-400">Unique IPs</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-700/50">
              <p className={`text-2xl font-bold ${(logStats?.error_count || 0) > 0 ? 'text-red-400' : 'text-white'}`}>
                {logStats?.error_count || 0}
              </p>
              <p className="text-xs text-surface-400">Errors</p>
            </div>
          </div>
          <div className="mt-4 p-3 rounded-lg bg-surface-700/50">
            <p className="text-sm text-surface-400">Average Response Time</p>
            <p className="text-xl font-bold text-amber-400">{logStats?.avg_response_time_ms || 0}ms</p>
          </div>
        </motion.div>

        {/* Top Endpoints */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-5 rounded-xl bg-surface-800/50 border border-surface-700"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-amber-400" />
            Top Endpoints
          </h2>
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {logStats?.top_paths?.slice(0, 8).map((item, index) => (
              <div
                key={item.path}
                className="flex items-center justify-between p-2 rounded-lg bg-surface-700/30 hover:bg-surface-700/50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-surface-500 w-5">{index + 1}.</span>
                  <span className="text-sm text-surface-300 truncate">{item.path}</span>
                </div>
                <span className="text-sm font-medium text-amber-400 ml-2">{item.count}</span>
              </div>
            ))}
            {(!logStats?.top_paths || logStats.top_paths.length === 0) && (
              <p className="text-center text-surface-500 py-4">No data yet</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Request Methods & Top Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-5 rounded-xl bg-surface-800/50 border border-surface-700"
        >
          <h2 className="text-lg font-semibold text-white mb-4">Requests by Method</h2>
          <div className="flex flex-wrap gap-3">
            {logStats?.requests_by_method && Object.entries(logStats.requests_by_method).map(([method, count]) => {
              const methodColors: Record<string, string> = {
                GET: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                POST: 'bg-green-500/20 text-green-400 border-green-500/30',
                PUT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                PATCH: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                DELETE: 'bg-red-500/20 text-red-400 border-red-500/30',
              };
              return (
                <div
                  key={method}
                  className={`px-4 py-2 rounded-lg border ${methodColors[method] || 'bg-surface-700/50 text-surface-300 border-surface-600'}`}
                >
                  <span className="font-mono font-medium">{method}</span>
                  <span className="ml-2 opacity-70">{count}</span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Top Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-5 rounded-xl bg-surface-800/50 border border-surface-700"
        >
          <h2 className="text-lg font-semibold text-white mb-4">Most Active Users</h2>
          <div className="space-y-2">
            {logStats?.top_users?.slice(0, 5).map((item, index) => (
              <div
                key={item.username}
                className="flex items-center justify-between p-2 rounded-lg bg-surface-700/30"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-amber-500 text-black' :
                    index === 1 ? 'bg-surface-400 text-black' :
                    index === 2 ? 'bg-amber-700 text-white' :
                    'bg-surface-600 text-surface-300'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="text-sm text-surface-300">{item.username}</span>
                </div>
                <span className="text-sm font-medium text-surface-400">{item.count} requests</span>
              </div>
            ))}
            {(!logStats?.top_users || logStats.top_users.length === 0) && (
              <p className="text-center text-surface-500 py-4">No data yet</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Links */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="p-5 rounded-xl bg-surface-800/50 border border-surface-700"
      >
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/users"
            className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
          >
            Manage Users
          </Link>
          <Link
            to="/admin/families"
            className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
          >
            Manage Families
          </Link>
          <Link
            to="/admin/logs"
            className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
          >
            View Logs
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

